/**
 * Pure functions for social contribution calculations.
 * No Vue dependency.
 */

import dayjs from 'dayjs';

/**
 * Compute the base amount for a social contribution based on its type.
 */
export function computeContributionBase(contribution, grossTotal, detaxBase) {
  switch (contribution.base) {
    case 'total':
      return grossTotal;
    case 'base_normal_9825':
      return (grossTotal - detaxBase) * 0.9825;
    case 'base_detax_9825':
      return detaxBase * 0.9825;
    case 'base_exonerated_heures_sup_comp':
      return detaxBase;
    case 'base_social':
      return 0;
    default:
      return grossTotal;
  }
}

/**
 * Check if a social contribution is relevant for the given care type and region.
 */
export function isContributionRelevant(contribution, region, careType = 'assmat') {
  if (contribution.care_type !== careType && contribution.care_type !== '') return false;
  if (contribution.exclude_regions && contribution.exclude_regions.includes(region)) return false;
  if (contribution.regions_only && !contribution.regions_only.includes(region)) return false;

  const now = dayjs();
  if (typeof contribution.start_date !== 'undefined' && now.isBefore(dayjs(contribution.start_date))) return false;
  if (typeof contribution.end_date !== 'undefined' && now.isAfter(dayjs(contribution.end_date))) return false;

  return true;
}

/**
 * Compute the amount of a single contribution for a given role (employer/employee).
 */
export function computeContributionAmount(contribution, role, grossTotal, detaxBase, region, careType = 'assmat') {
  if (!isContributionRelevant(contribution, region, careType)) return 0;

  const rate = contribution[role + '_rate'];
  if (rate === 0) return 0;

  const base = computeContributionBase(contribution, grossTotal, detaxBase);
  const amount = base * (rate / 100.0);
  const maximumAmount = contribution[role + '_max'] ?? amount;

  return Math.min(amount, maximumAmount);
}

/**
 * Compute total contributions across all contribution types for a given role.
 */
export function computeTotalContributions(contributions, role, grossTotal, detaxBase, region, careType = 'assmat') {
  let total = 0;
  for (const name in contributions) {
    total += computeContributionAmount(contributions[name], role, grossTotal, detaxBase, region, careType);
  }
  return total;
}
